import { createSelector } from "reselect";
import {
    ACCUTRADE_KEY_NON_PROD,
    ACCUTRADE_KEY_PROD,
    ACCUTRADE_NON_PROD,
    ACCUTRADE_PROD,
    CARS_TEST_DEALER_UUID,
} from "src/components/AccuTrade/accuTradeHelpers";
import { ApplicationStore } from "src/store";
import { CountryCode } from "Types/api/Account";
import { isLockedPricingExperimentEnabled } from "./dealExperiments";
import { getAccuTradeIcoUuid, getTradeInVin } from "src/store/selectors/tradeIn";

const ACCUTRADE_ICO_NON_PROD = "https://perseus-consumer-qa.rhcapl.com";

const ACCUTRADE_ICO_PROD = "https://cashoffer.accu-trade.com";

export const getCountry = (state: ApplicationStore): CountryCode => state.account?.address?.country || CountryCode.US;

export const IsCountryCanada = (state: ApplicationStore) => state.account?.address?.country === "CA";

export const IsCountryUS = (state: ApplicationStore) => state.account?.address?.country === "US";

export const hasSoftCreditIntegration = (state: ApplicationStore): boolean => {
    return !!state.account.integration_settings?.SoftCredit;
};

export const hasSubaruGTPIntegration = (state: ApplicationStore): boolean =>
    !!state.account.integration_settings?.SubaruGTP;

export const hasSubaruAccessories = (state: ApplicationStore): boolean =>
    !!state.account.integration_settings?.SubaruAccessories;

// @see https://github.com/carsdotcom/modern-retailing-shared/tree/master/libraries/mti-form#readme
export const getTradeProgram = createSelector(hasSubaruGTPIntegration, (gtpIntegration): "Subaru" | "none" =>
    gtpIntegration ? "Subaru" : "none"
);

export const hasModernTradeInIntegration = (state: ApplicationStore): boolean => {
    return !!state.account.integration_settings?.TradeIn;
};

export const hasAccuTradeICOIntegration = (state: ApplicationStore): boolean => {
    return !!state.account?.integration_settings?.AccuTradeInstantCashOffer;
};

export const getAccuTradeICOIntegration = (state: ApplicationStore) => {
    return state.account.integration_settings?.AccuTradeInstantCashOffer;
};

export const getCcid = (state: ApplicationStore) => {
    return state.account.ccid;
};

export const getAccuTradeBaseUrl = (state: ApplicationStore) =>
    getAccuTradeICOIntegration(state)?.environment === "Prod" ? ACCUTRADE_PROD : ACCUTRADE_NON_PROD;

export const getAccuTradeFrameSrcUrl = createSelector(
    [getAccuTradeICOIntegration, getAccuTradeBaseUrl, getCcid, getTradeInVin, getAccuTradeIcoUuid],
    (accuTradeIcoIntegration, baseUrl, ccid, vin, icoUuid) => {
        const cvin = vin ? `&cvin=${vin}` : "";

        const vdpid = `&vdpid=${ccid}`;

        if (accuTradeIcoIntegration) {
            return `${baseUrl}${!!icoUuid ? `/offer/cert/${icoUuid}` : ""}?dlr=${
                accuTradeIcoIntegration.environment === "Prod" ? ACCUTRADE_KEY_PROD : ACCUTRADE_KEY_NON_PROD
            }${accuTradeIcoIntegration.environment === "Prod" ? vdpid : `&uuid=${CARS_TEST_DEALER_UUID}`}${cvin}`;
        }
        return "";
    }
);

export const getAccountCountry = (state: ApplicationStore) => state.account.address?.country;

export const getAccountUrl = (state: ApplicationStore) => state.account.url;

export const getAccountHours = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin].account?.integration_settings?.Hours.days;

export const getAccountSlug = (state: ApplicationStore) => state.account.slug;

// locked_pricing is a json_logic rule object not a boolean
export const isLockedPricingSetup = (state: ApplicationStore) =>
    isLockedPricingExperimentEnabled(state) || !!state.account.integration_settings?.locked_pricing;

export const getAccountZipCode = (state: ApplicationStore) => state.account.address?.zip!;

export const getAccountPhoneNumber = (state: ApplicationStore): string => state.account.phone ?? "";

export const isAccountLoading = (state: ApplicationStore) => state.account.loading;
